import { type AggResponse, fetchAgg } from '../../../common/utils/fetch-agg';
import { inMemoryCache } from '../../../utils/caching/in-memory';

export type ConfluenceLabelsQueryVariables = {
	cloudId: string;
	limit?: number;
	searchText: string;
};

export const labelsCache: {
	clear: (key?: string) => void;
	get: (key: string) => Promise<AggResponse<ConfluenceLabelsResponse>> | undefined;
	has: (key: string) => boolean;
	inMemoryDecorator: (
		key: string,
		supplier: () => Promise<AggResponse<ConfluenceLabelsResponse>>,
		expireWhen?: ((result: AggResponse<ConfluenceLabelsResponse>) => boolean) | undefined,
	) => Promise<AggResponse<ConfluenceLabelsResponse>>;
	set: (key: string, result: Promise<AggResponse<ConfluenceLabelsResponse>>) => void;
} = inMemoryCache<AggResponse<ConfluenceLabelsResponse>>();

const GQL_CF_LABEL_OPERATION_NAME = 'SearchLabelsQuery';
const getConfluenceLabelsQuery = () => `
query ${GQL_CF_LABEL_OPERATION_NAME}($cloudId: ID! $searchText: String!, $limit: Int) {
  confluence {
    searchLabels(cloudId: $cloudId, searchText: $searchText, limit: $limit) @optIn(to: "ConfluenceExperimentalSearchLabels") {
	  otherLabels {
        label
      }
      suggestedLabels {
        label
      }
    }
  }
}
`;

export type ConfluenceLabel = {
	id?: string;
	label?: string;
};

export type ConfluenceLabelsResponse = {
	confluence: {
		searchLabels: {
			otherLabels?: ConfluenceLabel[];
			suggestedLabels?: ConfluenceLabel[];
		};
	};
};

export const confluenceLabelsQuery = (
	cloudId: string,
	searchText: string,
	limit?: number,
): Promise<AggResponse<ConfluenceLabelsResponse>> => {
	return labelsCache.inMemoryDecorator(searchText, async () => {
		const response = await fetchAgg<ConfluenceLabelsQueryVariables, ConfluenceLabelsResponse>({
			graphQuery: getConfluenceLabelsQuery(),
			variables: { cloudId, searchText, limit },
			operationName: GQL_CF_LABEL_OPERATION_NAME,
		});

		const hasErrors = response.errors?.some((error) => error);

		if (hasErrors) {
			return Promise.reject(response);
		}
		return response;
	});
};
